import { Binary } from 'lucide-react';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface Props {
  toasts: Toast[];
}

/**
 * M1 (hard launch v2): a11y on transient announcements.
 *
 * Toasts must reach screen readers without stealing focus. We split
 * the stack into two live regions:
 *   - role="status" / aria-live="polite" for info+success (non-urgent)
 *   - role="alert"  / aria-live="assertive" for warning+error (urgent)
 *
 * Per-toast role mirrors that grouping so VoiceOver/NVDA announce them
 * with the right urgency. The visual presentation is unchanged.
 */
function ToastStack({ toasts }: Props) {
  const polite = toasts.filter(t => t.type === 'info' || t.type === 'success');
  const assertive = toasts.filter(t => t.type === 'warning' || t.type === 'error');

  const renderToast = (t: Toast, isAssertive: boolean) => (
    <div
      key={t.id}
      role={isAssertive ? 'alert' : 'status'}
      aria-live={isAssertive ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`flex items-center gap-3 px-6 py-4 rounded-full border backdrop-blur-xl pointer-events-auto
        ${
          t.type === 'success'
            ? 'bg-ninpo-lime/10 border-ninpo-lime text-ninpo-lime'
            : t.type === 'warning' || t.type === 'error'
            ? 'bg-ninpo-red/10 border-ninpo-red text-ninpo-red'
            : 'bg-white/10 border-white/20 text-white'
        }`}
    >
      <Binary className="w-4 h-4" aria-hidden="true" />
      <span className="text-[10px] font-black uppercase tracking-widest">
        {t.message}
      </span>
    </div>
  );

  return (
    <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[11000] flex flex-col gap-2 w-full max-w-xs px-4 pointer-events-none">
      <div role="region" aria-live="polite" aria-atomic="false" className="flex flex-col gap-2">
        {polite.map(t => renderToast(t, false))}
      </div>
      <div role="region" aria-live="assertive" aria-atomic="false" className="flex flex-col gap-2">
        {assertive.map(t => renderToast(t, true))}
      </div>
    </div>
  );
}

export default ToastStack;
